import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import { reelExemptElementIds } from '@src/services/reel/reelExemptElementIds';
import { reelSectionContents } from '@src/services/reel/reelSectionContents';
import { extractErrorInfo } from '@src/utility/error';

import { VideoEditorLogger } from '../../../utility/VideoEditorLogger';
import { TranscriptTrack } from '../../models/TranscriptTrack';
import { EditorChangeType } from '../../types';
import { AbstractCommand } from '../AbstractCommand';
import { clampSceneAnchors } from '../lib/clampSceneAnchors';
import { applyReelSectionOrder } from './applyReelSectionOrder';

import type { Element } from '../../models/Element';
import type { Scene } from '../../models/layout/Scene';
import type { VideoEditor } from '../../VideoEditor';

/**
 * PLAT-11060 — drops one Section out of the reel: the passage, and everything
 * the creator put inside it.
 *
 * A removal is {@link applyReelSectionOrder} handed an order with the Section
 * left out — the helper rebuilds the transcript track from that order (so the
 * omitted Section's elements simply are not on the new track), re-lays what
 * remains contiguously from the reel's start and re-projects (DE3).
 *
 * The one thing the helper does not do is drop the removed Section's **owned**
 * elements: it carries them to `newSectionStart.get(section.id)`, which is
 * `undefined` for a Section that is no longer in the order, so it returns early
 * and leaves them on the timeline at their old times — floating over whatever
 * passage slid into that slot. Removing them is this command's own work, and it
 * is the whole of DS11.
 *
 * Undo restores the pre-command timeline snapshot taken by
 * {@link AbstractCommand}.
 */
export class RemoveReelSectionCommand extends AbstractCommand {
  private sectionId: string;
  /** Reel-level elements (template intros / end cards) exempt from carry (DE7). */
  private exemptElementIds: ReadonlySet<string> | undefined;

  private executed = false;
  private originalTimelineDuration: number | null = null;
  /**
   * The transcript track as it stood before the removal — and with it the
   * elements the removal took off the timeline.
   *
   * {@link AbstractCommand}'s snapshot restores element *state* by id, and
   * re-creates anything it cannot find as a plain {@link Element}: enough for a
   * reorder, where every id survives, but not for a removal, where the departed
   * transcript elements would come back as generic elements with no media times
   * and no clip segment — invisible to `deriveReelSections`. So the track
   * instance goes back first, the same way
   * {@link RestoreTranscriptFromOriginalCommand} restores its own previous
   * track, and the snapshot then finds every id it is looking for.
   */
  private previousTranscriptTrack: TranscriptTrack | undefined;
  /** The Section's owned elements, with the track each came off. */
  private removedOwned: { trackId: string; element: Element }[] = [];
  /**
   * The scenes as they stood before the removal re-anchored them.
   * {@link TimelineSnapshot} carries tracks and elements but not scenes, so the
   * snapshot {@link AbstractCommand} restores would leave the clamped set in
   * place — an undo that puts the passage back but not the layouts on it.
   */
  private previousScenes: Scene[] | undefined;

  constructor(
    editor: VideoEditor,
    sectionId: string,
    exemptElementIds?: ReadonlySet<string>,
  ) {
    super(editor);
    this.sectionId = sectionId;
    this.exemptElementIds = exemptElementIds;
  }

  getName(): string {
    return 'RemoveReelSectionCommand';
  }

  getDescription(): string {
    return `Remove reel section ${this.sectionId}`;
  }

  execute(): boolean {
    if (this.executed) return false;

    const timeline = this.editor.timeline;
    const sections = deriveReelSections(timeline);
    const section = sections.find(s => s.id === this.sectionId);

    if (!section) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'section_not_found',
        sectionId: this.sectionId,
      });
      return false;
    }

    // A reel of zero Sections is a clip with an empty timeline — not a state
    // anything downstream expects, and not one the creator can recover from
    // inside the panel (DS10). The menu disables the item, so this is the
    // command's own guard rather than the path a click takes.
    if (sections.length === 1) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'last_section',
        sectionId: this.sectionId,
      });
      return false;
    }

    const track = timeline.tracks.find(
      (t): t is TranscriptTrack => t instanceof TranscriptTrack,
    );
    if (!track) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'no_transcript_track',
      });
      return false;
    }

    super.execute(); // captures the timeline snapshot for undo

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        sectionId: this.sectionId,
        index: section.index,
        sectionCount: sections.length,
      });

      this.previousTranscriptTrack = track;
      this.originalTimelineDuration = timeline.duration ?? null;

      // Read **before** the re-layout: ownership is by time span (DE7), so
      // after it this same call would return whichever elements now sit in that
      // span — a silently wrong answer rather than an empty one.
      //
      // The same derived exemptions `applyReelSectionOrder` uses are passed to `reelSectionContents` so the intro card, the end card and the logo are not treated as Section contents
      const owned = reelSectionContents(timeline, section, {
        excludeElementIds: reelExemptElementIds(
          timeline.tracks,
          this.editor.getTrackManager().extraElementDataRaw ?? {},
          this.exemptElementIds,
        ),
      });

      // Scenes **before** anything leaves, for the reason
      // `clampSceneAnchors` gives: a scene anchor holds an element id, and
      // `AnchorResolver` throws on one the timeline no longer has — so a
      // Section removed out from under a scene does not merely mislay a layout,
      // it takes the whole preview serialization down the next time
      // `refreshTimeline` runs (`toPreviewJSON` → `applyLayoutSegmentsToScenes`).
      // A scene wholly inside the departing passage is dropped; one that only
      // reached into it is pulled back onto the neighbour it still covers.
      const departing = new Set([
        ...section.elementIds,
        ...owned.map(element => element.id),
      ]);
      const surviving = new Set(
        timeline.allElements
          .map(element => element.id)
          .filter(id => !departing.has(id)),
      );
      this.previousScenes = [...timeline.scenes];
      timeline.setScenes(
        clampSceneAnchors(timeline.scenes, surviving, timeline),
      );

      const { restored, duration } = applyReelSectionOrder(
        this.editor,
        track,
        sections,
        sections.filter(s => s.id !== this.sectionId),
        this.exemptElementIds,
      );

      // DS11 — a graphic that existed to sit over these words has no meaning
      // once they are gone, and leaving it behind would drop it onto whatever
      // passage slid into that time. The track each came off is noted so undo
      // can put it back where it was rather than on a guess.
      for (const element of owned) {
        const ownerTrackId = timeline.getTrackForElement(element.id)?.id;
        if (ownerTrackId) {
          this.removedOwned.push({ trackId: ownerTrackId, element });
        }
        timeline.removeElement(element.id);
      }

      // a removal makes the reel shorter
      this.recalculateTimeline();

      if (!restored) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'restore_transcript_failed',
        });
      }

      this.editor.markDirty('remove_reel_section');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'reel_section_removed',
        sectionId: this.sectionId,
        index: section.index,
      });

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', {
        sectionId: this.sectionId,
        removedElementCount: owned.length,
        duration,
      });
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'remove_threw',
        ...extractErrorInfo(error),
      });
      return false;
    }
  }

  undo(): boolean {
    if (!this.executed) return false;

    const timeline = this.editor.timeline;

    // Everything the removal took off the timeline goes back **before** the
    // snapshot runs, so it restores state onto the real elements instead of
    // re-creating them as generic ones (see `previousTranscriptTrack`).
    if (this.previousTranscriptTrack) {
      timeline.replaceTrack(
        this.previousTranscriptTrack.id,
        this.previousTranscriptTrack,
      );
      this.editor.setTranscriptTrack(this.previousTranscriptTrack.id);
    }
    for (const { trackId, element } of this.removedOwned) {
      timeline.addElement(trackId, element);
    }

    const result = super.undo();

    // The snapshot restores the tracks but neither the duration the removal
    // shortened nor the scenes it re-anchored.
    if (result && this.originalTimelineDuration !== null) {
      timeline.setDuration(this.originalTimelineDuration);
    }
    if (result && this.previousScenes) {
      timeline.setScenes(this.previousScenes);
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
